import React, { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBolt, faRobot, faGears, faCircleCheck, faGaugeHigh, faShieldHalved, faArrowRightLong, faPlay } from '@fortawesome/free-solid-svg-icons'
import { faGoogle, faLinkedin, faMeta, faGithub, faFacebook, faYoutube } from '@fortawesome/free-brands-svg-icons'
import { addAutomationAudit } from '../services/firestoreService'

const FEATURES = [
  { icon: faBolt, title: 'Rapid ROI', desc: 'Automate repetitive work in days, not months.' },
  { icon: faGaugeHigh, title: 'Scale Without Hiring', desc: 'Let bots handle busywork while your team handles growth.' },
  { icon: faShieldHalved, title: 'Secure & Compliant', desc: 'Best-practice auth, logging, and data handling.' },
  { icon: faCircleCheck, title: 'Done-For-You', desc: 'Scoping, build, QA, training, and handover included.' },
]

const SERVICES = [
  { icon: faRobot, title: 'AI Assistants & Agents', desc: 'Lead intake, support triage, quoting, summaries, drafting, and approvals.' },
  { icon: faGears, title: 'Workflow Automation', desc: 'CRM, email, Sheets, Slack, Notion, Make/Zapier, webhooks, and custom APIs.' },
  { icon: faBolt, title: 'RPA & Data Pipelines', desc: 'Scraping, ETL, enrichment, alerts, and dashboards.' },
]

const PROCESS = [
  { step: '01', title: 'Free Automation Audit', desc: '15-minute discovery to spot quick wins.' },
  { step: '02', title: 'Blueprint', desc: 'Clear scope, KPI targets, timeline, and fixed quote.' },
  { step: '03', title: 'Build', desc: 'Iterative delivery with weekly demos.' },
  { step: '04', title: 'Launch & Train', desc: 'UAT, docs, training, and success checklist.' },
]

const FAQS = [
  { q: 'How fast can we launch?', a: 'Simple automations go live in 3–7 days; larger builds in 2–4 weeks.' },
  { q: 'Do you work with our stack?', a: 'Yes. HubSpot, Salesforce, Pipedrive, Sheets, Airtable, Slack, Notion, Make/Zapier, custom APIs, and more.' },
  { q: 'What does pricing look like?', a: 'Fixed-fee sprints for builds, optional monthly care plans for monitoring and tweaks.' },
  { q: 'Can you help with ads and funnels?', a: 'Yes. We provide the landing page, lead magnet, and tracking to plug into Google/Meta.' },
]

const TRUST = [
  { icon: faGoogle, label: 'Google-grade mindset' },
  { icon: faMeta, label: 'Meta & Make experience' },
  { icon: faLinkedin, label: 'B2B proven playbooks' },
  { icon: faGithub, label: 'Secure engineering' },
]

export default function AutomationLanding() {
  const initial = { name: '', email: '', company: '', interest: 'AI Assistants', notes: '', source: 'AI AGENCY DIRECTORY | Free Automation Audit' }
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [auditData, setAuditData] = useState(initial)

  const handleChange = e => {
    const { name, value } = e.target
    setAuditData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async e => {
    e.preventDefault()
    if (submitting) return
    setSubmitting(true)
    try {
      await addAutomationAudit(auditData)
      console.log('AI AGENCY DIRECTORY | Audit Form Submission', auditData)
      setSubmitted(true)
      setAuditData(initial)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
      <Helmet>
        <title>Automation Services | Free Audit — AI AGENCY DIRECTORY</title>
        <meta name="description" content="AI AGENCY DIRECTORY: Done-for-you automation—AI assistants, workflows, and data pipelines. Book a free automation audit." />
      </Helmet>

      <section className="relative overflow-hidden" style={{ backgroundImage: 'url("./assets/consul.png")' }}>
        <div className="absolute inset-0 bg-primary opacity-80" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 text-white">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h1 className="text-4xl md:text-6xl font-extrabold leading-tight">Automate Work. <span className="text-blue-400">Grow Faster.</span></h1>
              <p className="mt-5 text-lg md:text-xl text-slate-200">We design and ship AI assistants, workflow automations, and data pipelines that remove busywork and unlock revenue—fast.</p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <a href="#audit" className="inline-flex items-center justify-center px-7 py-4 rounded-full bg-blue-600 hover:bg-blue-700 transition font-semibold">Get Free Automation Audit <FontAwesomeIcon icon={faArrowRightLong} className="ml-3" /></a>
                <a href="#demo" className="inline-flex items-center justify-center px-7 py-4 rounded-full bg-white/10 hover:bg-white/20 transition font-semibold">Watch 60-sec Demo <FontAwesomeIcon icon={faPlay} className="ml-3" /></a>
              </div>
              <div className="mt-8 flex flex-wrap items-center gap-5">
                {TRUST.map(t => (
                  <div key={t.label} className="flex items-center gap-2 text-slate-300">
                    <FontAwesomeIcon icon={t.icon} />
                    <span className="text-sm">{t.label}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="aspect-video rounded-3xl overflow-hidden ring-1 ring-white/10 shadow-2xl">
                <video src="./av.mp4" className="w-full h-full object-cover" autoPlay loop muted playsInline />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white text-slate-900 rounded-2xl p-4 shadow-xl">
                <div className="text-sm font-semibold">Avg time saved per role</div>
                <div className="text-3xl font-extrabold">6–12 hrs/week</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-5xl font-bold text-secondary text-center">Why Automation</h2>
        <p className="text-center text-slate-600 mt-4 max-w-2xl mx-auto">Replace manual clicks with reliable, trackable workflows. Start small, scale quickly, measure ROI.</p>
        <div className="grid md:grid-cols-4 gap-6 mt-12">
          {FEATURES.map(f => (
            <div key={f.title} className="p-6 rounded-2xl border border-slate-200 bg-white hover:shadow-xl transition">
              <div className="w-14 h-14 rounded-full bg-primary text-white flex items-center justify-center">
                <FontAwesomeIcon icon={f.icon} className="text-2xl" />
              </div>
              <h3 className="mt-4 text-xl font-bold text-secondary">{f.title}</h3>
              <p className="mt-2 text-slate-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="demo" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="space-y-5">
            <h2 className="text-3xl md:text-5xl font-bold text-secondary">What We Build</h2>
            <p className="text-slate-600">High-impact automations mapped to your funnel, ops, finance, and support. We connect your tools and ship measurable outcomes.</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {SERVICES.map(s => (
                <div key={s.title} className="p-5 rounded-2xl border border-slate-200 bg-white">
                  <div className="w-12 h-12 rounded-full bg-primary text-white flex items-center justify-center">
                    <FontAwesomeIcon icon={s.icon} />
                  </div>
                  <div className="mt-3 font-bold">{s.title}</div>
                  <div className="text-sm text-slate-600 mt-1">{s.desc}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-3">
              <a href="#audit" className="px-6 py-3 rounded-full bg-primary text-white font-semibold">Book Audit</a>
              <a href="#audit" className="px-6 py-3 rounded-full border border-slate-300 font-semibold">Contact</a>
            </div>
          </div>
          <div className="aspect-video rounded-3xl overflow-hidden ring-1 ring-slate-200">
            <img src="./wf.jpg" alt="Workflow dashboard" className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="rounded-3xl bg-gradient-to-br from-blue-600 to-indigo-600 p-10 md:p-14 text-white">
          <div className="grid md:grid-cols-4 gap-8 items-center">
            {PROCESS.map(p => (
              <div key={p.step} className="bg-white/10 rounded-2xl p-6">
                <div className="text-sm opacity-80">Step {p.step}</div>
                <div className="mt-1 font-bold text-xl">{p.title}</div>
                <div className="mt-2 text-sm opacity-90">{p.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-5xl font-bold text-secondary text-center">Proof & Praise</h2>
        <p className="text-center text-slate-600 mt-4 max-w-2xl mx-auto">Teams cut manual tasks, speed up response times, and reduce errors with our automations.</p>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          <div className="p-6 rounded-2xl border border-slate-200 bg-white">
            <div className="text-sm text-slate-500">Ops Automation</div>
            <div className="mt-2 text-2xl font-extrabold text-secondary">-42% handling time</div>
            <p className="mt-3 text-slate-600">Email → CRM → Slack routing with summaries and next steps.</p>
          </div>
          <div className="p-6 rounded-2xl border border-slate-200 bg-white">
            <div className="text-sm text-slate-500">Sales Assist</div>
            <div className="mt-2 text-2xl font-extrabold text-secondary">+28% qualified demos</div>
            <p className="mt-3 text-slate-600">Instant lead enrichment, scoring, and sequenced outreach.</p>
          </div>
          <div className="p-6 rounded-2xl border border-slate-200 bg-white">
            <div className="text-sm text-slate-500">Support Triage</div>
            <div className="mt-2 text-2xl font-extrabold text-secondary">-35% resolution time</div>
            <p className="mt-3 text-slate-600">Auto-summaries, suggested replies, SLA alerts, and tagging.</p>
          </div>
        </div>
      </section>

      <section id="audit" className="max-w-7xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <h2 className="text-3xl md:text-5xl font-bold text-secondary">Free Automation Audit</h2>
            <p className="mt-4 text-slate-600">Get a 1-page blueprint with 3–5 automation wins, projected ROI, and timeline. No obligation.</p>
            <ul className="mt-6 space-y-3">
              <li className="flex items-start gap-3"><FontAwesomeIcon icon={faCircleCheck} className="mt-1 text-green-600" /><span>Tool mapping and quick-win list</span></li>
              <li className="flex items-start gap-3"><FontAwesomeIcon icon={faCircleCheck} className="mt-1 text-green-600" /><span>Effort vs impact scorecard</span></li>
              <li className="flex items-start gap-3"><FontAwesomeIcon icon={faCircleCheck} className="mt-1 text-green-600" /><span>Fixed-fee build estimate</span></li>
            </ul>
            <button className="px-6 py-3 rounded-full bg-primary text-white font-semibold mt-4" onClick={() => window.location.href = 'https://calendly.com/lloca-aiagenciesdirectory/30min' }>or Book Call</button>
          </div>
          <form onSubmit={handleSubmit} className="p-6 rounded-3xl border border-slate-200 bg-white shadow-sm">
            {submitted && <div className="mb-4 rounded-xl bg-green-50 text-green-800 border border-green-200 px-4 py-3 text-sm">Thanks! Your request has been received. We will email you shortly.</div>}
            <div className="grid gap-4">
              <input name="name" value={auditData.name} onChange={handleChange} placeholder="Your Name" required className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input name="email" type="email" value={auditData.email} onChange={handleChange} placeholder="Work Email" required className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <input name="company" value={auditData.company} onChange={handleChange} placeholder="Company" className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <select name="interest" value={auditData.interest} onChange={handleChange} className="w-full px-4 py-3 rounded-xl border border-slate-300 focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option>AI Assistants</option>
                <option>Workflow Automation</option>
                <option>Data Pipelines</option>
                <option>End-to-End Funnel</option>
              </select>
              <textarea name="notes" value={auditData.notes} onChange={handleChange} placeholder="What would you like to automate?" className="w-full px-4 py-3 rounded-xl border border-slate-300 h-28 focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <button type="submit" disabled={submitting} className="w-full px-6 py-4 rounded-xl bg-primary text-white font-semibold">{submitting ? 'Submitting...' : submitted ? 'Submitted' : 'Get My Free Audit'}</button>
            </div>
          </form>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <h2 className="text-3xl md:text-5xl font-bold text-secondary text-center">FAQs</h2>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {FAQS.map(f => (
            <details key={f.q} className="p-6 rounded-2xl border border-slate-200 bg-white">
              <summary className="font-semibold cursor-pointer">{f.q}</summary>
              <p className="mt-3 text-slate-600">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <footer className="px-6 py-14 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img src="./logo.png" alt="AI AGENCY DIRECTORY" className="h-12 object-cover" />
          </div>
          <div className="flex gap-4">
            <a href="https://www.linkedin.com/company/ai-agency-directory/?viewAsMember=true" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-[#0A66C2] text-white flex items-center justify-center"><FontAwesomeIcon icon={faLinkedin} /></a>
            <a href="https://www.instagram.com/aiagencydirectory?igsh=dWcwdnBoemkybTk4&utm_source=qr" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center"><FontAwesomeIcon icon={faFacebook} /></a>
            <a href="https://youtube.com/@theshahpodcast?si=TqHsa3i1dE732tS3" target="_blank" rel="noreferrer" className="w-10 h-10 rounded-full bg-red-600 text-white flex items-center justify-center"><FontAwesomeIcon icon={faYoutube} /></a>
          </div>
          <a href="#audit" className="px-6 py-3 rounded-full bg-primary text-white font-semibold">Talk To Us</a>
        </div>
      </footer>
    </>
  )
}
